import type {
  BidderReportData,
  ReportSection,
} from 'components/molecules/bidding_summary_bidder_report_pdf/report_data';
import { HiringHistoryItem } from 'hooks/useHiringHistory';
import { HiringHistoryBiddingResponse } from 'services/actions/ai_management_hiring_history';
import { count, dateBr, money } from 'services/ai_management_format';
import { formatIsoDateTimeBr } from 'services/format_iso_date_br';

const BIDDING_COLUMNS = [
  { label: 'Licitação', width: 14 },
  { label: 'Data', width: 10 },
  { label: 'Valor negociado', width: 15, align: 'right' as const },
  { label: 'Qtd.', width: 7, align: 'right' as const },
  { label: 'Part.', width: 7, align: 'right' as const },
  { label: 'Vencedor', width: 22 },
  { label: 'Descrição do item', width: 25 },
];

const biddingRow = (bidding: HiringHistoryBiddingResponse) => [
  bidding.edital ?? 'Sem número',
  dateBr(bidding.data),
  money(bidding.valor),
  count(bidding.quantidade),
  count(bidding.participantes),
  bidding.vencedor ?? '—',
  bidding.descricao ?? '—',
];

const itemSection = (
  item: HiringHistoryItem,
  biddings: HiringHistoryBiddingResponse[],
): ReportSection => ({
  title: item.label,
  blocks: [
    {
      kind: 'rows',
      rows: [
        { label: 'Licitações', value: count(item.biddings) },
        { label: 'Itens contratados', value: count(item.lines) },
        { label: 'Valor médio contratado', value: money(item.averageValue) },
        { label: 'Valor mediano', value: money(item.medianValue) },
        { label: 'Total contratado', value: money(item.totalSpent) },
        { label: 'Última compra', value: dateBr(item.lastPurchaseAt) },
      ],
    },
    biddings.length
      ? { kind: 'table', columns: BIDDING_COLUMNS, rows: biddings.map(biddingRow) }
      : { kind: 'text', text: 'Nenhuma licitação anterior encontrada para este item.' },
  ],
});

export const buildHiringHistoryReport = ({
  items,
  biddingsByKey,
  biddingNumber,
  publicBody,
  conlicitacaoId,
}: {
  items: HiringHistoryItem[];
  biddingsByKey: Record<string, HiringHistoryBiddingResponse[]>;
  biddingNumber: string;
  publicBody: string;
  conlicitacaoId: string;
}): BidderReportData => ({
  biddingNumber,
  publicBody,
  conlicitacaoId,
  generatedAt: formatIsoDateTimeBr(new Date().toISOString()),
  sections: [
    {
      title: 'Histórico de Contratações',
      blocks: [
        {
          kind: 'text',
          text: `Itens contratados anteriormente pelo órgão${
            publicBody ? ` ${publicBody}` : ''
          }, selecionados no Histórico de Contratações da Gestão IA.`,
        },
      ],
    },
    ...items.map((item) => itemSection(item, biddingsByKey[item.key] ?? [])),
  ],
});
